/**
 * Delivery Countdown Timer logic for Shopify OS 2.0
 */
if (!customElements.get('delivery-countdown')) {
  class DeliveryCountdown extends HTMLElement {
    constructor() {
      super();
      this.interval = null;
      this._initialized = false;
      this._isConnected = false;
      this._onVariantChange = null;
      this._unsubscribeVariant = null;
      this._onDocumentVariantChange = null;
      this._io = null;
      this._visibilityHandler = null;
      this._tickInterval = 60000;
      this._warnedInvalidTimezone = false;
    }

    connectedCallback() {
      if (this._initialized) return;

      const enabledAttr = this.dataset.enabled;
      const isEnabled = (typeof enabledAttr === 'undefined') || enabledAttr === 'true' || enabledAttr === '1';
      if (!isEnabled) {
        try {
          this.style.display = 'none';
          this.setAttribute('hidden', '');
        } catch (e) {}
        return;
      }

      this._initialized = true;
      this._isConnected = true;

      requestAnimationFrame(() => { this.init(); });

      if (window.subscribe) {
        this._onVariantChange = ({ data }) => {
          if (!this._isConnected) return;
          if (data && data.variant) this.toggleVisibility(data.variant.available);
        };
        try {
          const unsub = subscribe(PUB_SUB_EVENTS.variantChange, this._onVariantChange);
          if (typeof unsub === 'function') this._unsubscribeVariant = unsub;
        } catch (e) {}
      }

      this._onDocumentVariantChange = (event) => {
        if (!this._isConnected) return;
        if (event.detail && event.detail.variant) this.toggleVisibility(event.detail.variant.available);
      };
      document.addEventListener('variant:change', this._onDocumentVariantChange);
    }

    disconnectedCallback() {
      if (this.interval) { clearInterval(this.interval); this.interval = null; }
      this._isConnected = false;

      if (this._onDocumentVariantChange) {
        try { document.removeEventListener('variant:change', this._onDocumentVariantChange); } catch (e) {}
        this._onDocumentVariantChange = null;
      }

      if (this._unsubscribeVariant && typeof this._unsubscribeVariant === 'function') {
        try { this._unsubscribeVariant(); } catch (e) {}
        this._unsubscribeVariant = null;
      } else if (this._onVariantChange && window.unsubscribe && typeof window.unsubscribe === 'function') {
        try { window.unsubscribe(PUB_SUB_EVENTS.variantChange, this._onVariantChange); } catch (e) {}
      }

      this._initialized = false;

      if (this._io) { try { this._io.disconnect(); } catch (e) {} this._io = null; }
      if (this._visibilityHandler) {
        try { document.removeEventListener('visibilitychange', this._visibilityHandler); } catch (e) {}
        this._visibilityHandler = null;
      }
    }

    toggleVisibility(available) {
      if (available) {
        this.style.display = 'block';
        this.removeAttribute('hidden');
      } else {
        this.style.display = 'none';
        this.setAttribute('hidden', '');
      }
    }

    init() {
      this.timerElement = this.querySelector('[data-timer]');
      if (!this.timerElement) {
        console.warn('Delivery Countdown: Timer element not found');
        return;
      }

      this.cutoffHour       = parseInt(this.dataset.cutoffHour) || 14;
      this.deliveryDaysMin  = parseInt(this.dataset.deliveryDaysMin) || 1;
      this.deliveryDaysMax  = parseInt(this.dataset.deliveryDaysMax) || 3;
      this.skipWeekends     = this.dataset.skipWeekends === 'true';
      this.holidays         = this.dataset.holidays
        ? this.dataset.holidays.split(',').map(d => d.trim()).filter(d => d)
        : [];
      this.timezone         = this.dataset.timezone || 'Asia/Dubai';

      const showSeconds     = this.dataset.showSeconds === 'true';
      this._tickInterval    = showSeconds ? 1000 : 60000;

      this._visibilityHandler = () => {
        if (document.visibilityState === 'hidden') {
          this.stopTimer();
        } else if (document.visibilityState === 'visible') {
          if (!this._io || this._isElementVisible) this.startTimer();
        }
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);

      if ('IntersectionObserver' in window) {
        if (!this._io) {
          this._isElementVisible = false;
          this._io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) { this._isElementVisible = true;  this.startTimer(); }
              else                      { this._isElementVisible = false; this.stopTimer();  }
            });
          }, { root: null, threshold: 0 });
        }
        try { this._io.observe(this); } catch (e) { this.startTimer(); }
      } else {
        this.startTimer();
      }

      console.log('Delivery Countdown initialized for', this.timezone, 'tickInterval', this._tickInterval);
    }

    startTimer() {
      try { this.updateCountdown(); } catch (e) {}
      if (this.interval) return;
      this.interval = setInterval(() => this.updateCountdown(), this._tickInterval);
    }

    stopTimer() {
      if (this.interval) { clearInterval(this.interval); this.interval = null; }
    }

    setCustomMessage(hours, minutes, deliveryDate) {
      const messageElement = this.querySelector('[data-custom-message]');
      if (!messageElement) return;

      if (hours < 1) {
        messageElement.textContent = `Hurry! Only ${minutes} minutes left for same-day delivery!`;
      } else if (hours < 3) {
        messageElement.textContent = `Quick! ${hours} hours and ${minutes} minutes left for delivery by ${deliveryDate.toLocaleDateString('en-AE', { weekday: 'long', day: 'numeric', month: 'long' })}`;
      } else {
        messageElement.textContent = `Order now to get your delivery by ${deliveryDate.toLocaleDateString('en-AE', { weekday: 'long', day: 'numeric', month: 'long' })}`;
      }
    }

    updateCountdown() {
      try {
        const now        = new Date();
        const targetTime = this._createZonedDate(this.timezone, now);

        if (isNaN(targetTime.getTime())) throw new Error('Invalid date conversion');

        const cutoff = new Date(targetTime);
        cutoff.setHours(this.cutoffHour, 0, 0, 0);

        let isBeforeCutoff = targetTime < cutoff;
        if (!isBeforeCutoff) cutoff.setDate(cutoff.getDate() + 1);

        const diff    = cutoff - targetTime;
        const hours   = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        const two     = (n) => String(n).padStart(2, '0');

        const deliveryDate = this.calculateDeliveryDate(targetTime, isBeforeCutoff);
        const dateString   = deliveryDate.toLocaleDateString('en-AE', {
          weekday: 'long', day: 'numeric', month: 'long'
        });

        // DOM structure check is the most reliable signal for inline mode.
        // Covers cases where data-display-style defaults to 'boxed' in Shopify admin
        // but the inline HTML markup is actually rendered.
        const displayStyle    = (this.dataset.displayStyle || '').toLowerCase().trim();
        const hasInlineMarkup = !!this.querySelector('.delivery-inline-wrapper');
        const isPremium       = displayStyle === 'premium';
        const isInline        = displayStyle === 'inline' || (hasInlineMarkup && !isPremium);
        const showSeconds     = this.dataset.showSeconds === 'true' || this._tickInterval === 1000;
        console.log('[DC] displayStyle=' + displayStyle + ' isInline=' + isInline + ' isPremium=' + isPremium);

        /* ─────────────────────────────────────────
           PREMIUM layout
        ───────────────────────────────────────── */
        if (isPremium) {
          const dateEl = this.querySelector('.date-value');
          if (dateEl) dateEl.textContent = dateString;

          const timerHtml = [];
          timerHtml.push(`<span class="time-box time-box--hours">${two(hours)}</span>`);
          timerHtml.push(`<span class="time-sep">:</span>`);
          timerHtml.push(`<span class="time-box time-box--minutes">${two(minutes)}</span>`);
          if (showSeconds) {
            timerHtml.push(`<span class="time-sep">:</span>`);
            timerHtml.push(`<span class="time-box time-box--seconds">${two(seconds)}</span>`);
          }

          if (this.timerElement) {
            this.timerElement.innerHTML = timerHtml.join('');
            if (isBeforeCutoff) this.classList.add('is-animating');
            else                this.classList.remove('is-animating');
          }

          const srEl = this.querySelector('.delivery-card__sr');
          if (srEl) {
            const parts = [];
            if (hours > 0)   parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
            if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
            if (showSeconds) parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
            const timePhrase = parts.length ? parts.join(', ') : `${minutes} minutes`;
            srEl.textContent = `Order in the next ${timePhrase}. Delivery by ${dateString}.`;
          }

        /* ─────────────────────────────────────────
           INLINE layout
           Expects:
             .delivery-inline-text > strong.date-value  → date only
             .delivery-inline-timer[data-timer]          → countdown only
        ───────────────────────────────────────── */
        } else if (isInline) {
          // Always re-query elements in case init() ran before DOM was ready
          const timerEl = this.querySelector('[data-timer]') || this.querySelector('.delivery-inline-timer');
          const dateEl  = this.querySelector('.date-value');

          if (dateEl) dateEl.textContent = dateString;

          if (timerEl) {
            // Always show countdown — before cutoff: "Order in Xh Ym Zs"
            // After cutoff: "Order in Xh Ym Zs for tomorrow" (counting to next day cutoff)
            const timeText = hours > 0
              ? `${two(hours)}h ${two(minutes)}m${showSeconds ? ` ${two(seconds)}s` : ''}`
              : `${two(minutes)}m${showSeconds ? ` ${two(seconds)}s` : ''}`;

            if (isBeforeCutoff) {
              timerEl.textContent = `Order in ${timeText}`;
              this.classList.add('is-animating');
            } else {
              timerEl.textContent = `Order in ${timeText}`;
              this.classList.remove('is-animating');
            }
          } else {
            console.warn('[DC] inline timerEl not found — check [data-timer] or .delivery-inline-timer in HTML');
          }

        /* ─────────────────────────────────────────
           DEFAULT / BOXED layout (fallback)
        ───────────────────────────────────────── */
        } else {
          if (isBeforeCutoff) {
            const timeLeftText = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
            this.timerElement.innerHTML = `Order within <span class="time-left">${timeLeftText}</span> for delivery by <strong>${dateString}</strong>`;
            this.classList.add('is-animating');
          } else {
            this.timerElement.innerHTML = `Order now for delivery by <strong>${dateString}</strong>`;
            this.classList.remove('is-animating');
          }
        }

        this.setCustomMessage(hours, minutes, deliveryDate);

      } catch (err) {
        console.error('Delivery Countdown Error:', err);
      }
    }

    _createZonedDate(timezone, now = new Date()) {
      const tryCreate = (tz) => {
        const opts = {
          timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric',
          hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23'
        };
        const parts = new Intl.DateTimeFormat('en-US', opts).formatToParts(now);
        const find  = (type) => parts.find(p => p.type === type)?.value;

        const year   = parseInt(find('year'),   10) || now.getFullYear();
        const month  = parseInt(find('month'),  10) || (now.getMonth() + 1);
        const day    = parseInt(find('day'),    10) || now.getDate();
        const hour   = parseInt(find('hour'),   10);
        const minute = parseInt(find('minute'), 10);
        const second = parseInt(find('second'), 10);

        const h = Number.isNaN(hour)   ? now.getHours()   : hour;
        const m = Number.isNaN(minute) ? now.getMinutes() : minute;
        const s = Number.isNaN(second) ? now.getSeconds() : second;

        return new Date(Date.UTC(year, month - 1, day, h, m, s));
      };

      try {
        return tryCreate(timezone);
      } catch (errPrimary) {
        try {
          if (timezone !== 'Asia/Dubai') {
            if (!this._warnedInvalidTimezone) {
              console.warn('Delivery Countdown: invalid timezone, falling back to Asia/Dubai for', timezone);
              this._warnedInvalidTimezone = true;
            }
            return tryCreate('Asia/Dubai');
          }
        } catch (errFallback) { /* fall through */ }

        if (!this._warnedInvalidTimezone) {
          console.warn('Delivery Countdown: timezone Intl failure for', timezone, errPrimary);
          this._warnedInvalidTimezone = true;
        }
        return new Date();
      }
    }

    calculateDeliveryDate(now, isBeforeCutoff) {
      let deliveryDate = new Date(now);
      let daysToAdd    = isBeforeCutoff ? this.deliveryDaysMin : (this.deliveryDaysMin + 1);
      let added = 0, safetyCounter = 0;

      while (added < daysToAdd && safetyCounter < 30) {
        deliveryDate.setDate(deliveryDate.getDate() + 1);
        if (this.isWorkDay(deliveryDate)) added++;
        safetyCounter++;
      }

      return deliveryDate;
    }

    isWorkDay(date) {
      const day = date.getDay();
      if (this.skipWeekends && (day === 6 || day === 0)) return false;
      const isoDate = date.toISOString().split('T')[0];
      if (this.holidays.includes(isoDate)) return false;
      return true;
    }
  }

  customElements.define('delivery-countdown', DeliveryCountdown);
}